import * as ss from 'superstruct';

/**
 * A primitive value that can appear in consumer-opaque config or metadata: a
 * string, number, or boolean. May be absent (`undefined`) or explicitly `null`.
 */
export const ScalarValue = ss.optional(
  ss.nullable(ss.union([ss.string(), ss.number(), ss.boolean()])),
);

export type ScalarValue = ss.Infer<typeof ScalarValue>;

/**
 * A bag of string-keyed primitive values — i.e.
 * `Record<string, string | number | boolean | null | undefined>`. Used for
 * config and metadata whose exact shape is owned by the destination/consumer
 * rather than this schema.
 */
export const ScalarRecord = ss.record(ss.string(), ScalarValue);

export type ScalarRecord = ss.Infer<typeof ScalarRecord>;

export const StartFrom = ss.enums(['beginning', 'end', 'timestamp']);

export type StartFrom = ss.Infer<typeof StartFrom>;

/**
 * Stored status of a per-conversion consumption event.
 *
 * `retryable` is a system-assigned, non-terminal state (consumers never return it):
 * a failed delivery — an initial `consume` error, a `checkPending` error, or a
 * pending timeout — that is still eligible for one automatic retry. The retry sweep
 * re-delivers it once; on failure it transitions to terminal `error`.
 */
export const ConsumptionStatus = ss.enums([
  'success',
  'error',
  'pending',
  'retryable',
]);

export type ConsumptionStatus = ss.Infer<typeof ConsumptionStatus>;

/**
 * Per-conversion result from an output-stream consumer.
 *
 * `pending` is for consumers whose downstream APIs are asynchronous: the
 * submission was accepted but the terminal outcome is not yet known. The
 * `pendingMetadata` is consumer-opaque and round-tripped to the consumer's
 * `checkPending` later.
 */
export const ConsumptionResult = ss.union([
  ss.object({
    status: ss.literal('success'),
  }),
  ss.object({
    status: ss.literal('error'),
    error: ss.optional(ss.string()),
  }),
  ss.object({
    status: ss.literal('pending'),
    pendingMetadata: ScalarRecord,
  }),
]);

export type ConsumptionResult = ss.Infer<typeof ConsumptionResult>;

/**
 * Holds the configuration information for a destination.
 *
 * Shape depends on the destination (dictated by name).
 */
export const OutputStreamDestinationConfig = ss.intersection([
  ss.type({
    /** Name of the destination. */
    name: ss.string(),
  }),
  ScalarRecord,
]);

export type OutputStreamDestinationConfig = ss.Infer<
  typeof OutputStreamDestinationConfig
>;
